import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Table, Info, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function SharedSample() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(`${API}/share/public/sample/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json())?.detail || "Link expired");
        return r.json();
      })
      .then(setData)
      .catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (err) {
    return (
      <div className="App min-h-screen grid place-items-center p-6">
        <div className="max-w-md text-center">
          <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">Sample link</div>
          <h1 className="font-heading font-black text-3xl mb-3">This link is no longer active</h1>
          <p className="text-muted-foreground text-sm">{err}</p>
          <Link to="/" className="mt-6 inline-block text-primary hover:underline">← Back to ExactReach</Link>
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-10 text-sm text-muted-foreground">Loading sample…</div>;

  return (
    <div className="App min-h-screen">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="share-logo">
            <div className="h-8 w-8 bg-foreground grid place-items-center rounded-sm">
              <span className="text-background font-heading font-extrabold text-sm">ER</span>
            </div>
            <span className="font-heading font-bold text-xl">ExactReach</span>
          </Link>
          <Link to="/#query">
            <Button size="sm" className="rounded-sm" data-testid="share-start-your-own">
              Start your own audience <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">Shared sample · {data.request_reference}</div>
        <h1 className="font-heading font-black tracking-tighter text-3xl md:text-5xl mb-4" data-testid="share-title">{data.title}</h1>
        {data.audience_description && (
          <p className="text-sm text-muted-foreground max-w-3xl font-mono whitespace-pre-wrap">{data.audience_description}</p>
        )}
        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm">
          {data.estimated_volume != null && (
            <div><span className="text-muted-foreground">Est. audience:</span> <b>{data.estimated_volume.toLocaleString()}</b></div>
          )}
          <div><span className="text-muted-foreground">Sample rows:</span> <b>{data.rows?.length || 0}</b></div>
          <div><span className="text-muted-foreground">File:</span> <span className="font-mono">{data.sample_filename}</span></div>
        </div>

        <div className="mt-8 border border-border bg-white">
          <div className="p-3 border-b border-border flex items-center gap-2">
            <Table size={16} weight="duotone" className="text-primary" />
            <div className="font-semibold text-sm">Sample preview</div>
            <div className="text-xs text-muted-foreground ml-auto">Read-only</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="share-sample-table">
              <thead className="bg-muted">
                <tr>
                  {data.columns?.map((c, i) => (
                    <th key={`col-${i}-${c || ""}`} className="p-2 text-left uppercase tracking-widest-2 text-[10px] text-muted-foreground font-semibold whitespace-nowrap">{c || `col ${i + 1}`}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Sample rows come from a read-only CSV parse — positional keys are safe here */}
                {(data.rows || []).map((row, ri) => (
                  <tr key={`row-${ri}`} className="border-t border-border">
                    {row.map((cell, ci) => (
                      <td key={`cell-${ri}-${ci}`} className="p-2 whitespace-nowrap max-w-xs truncate font-mono" title={cell}>{cell}</td>
                    ))}
                  </tr>
                ))}
                {(!data.rows || data.rows.length === 0) && (
                  <tr><td colSpan={data.columns?.length || 1} className="p-8 text-center text-muted-foreground text-sm">Sample could not be parsed. Ask the owner to check the file.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground border border-border p-3 bg-muted">
          <Info size={14} className="mt-0.5 shrink-0" />
          <div>{data.note}</div>
        </div>
      </section>
    </div>
  );
}
